import React, {
  createRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  useGetDocumentBlacklineQuery,
  useGetDocumentBlacklinesQuery,
  useGetDocumentBlacklineStatsQuery,
  useGetDocumentByUuidQuery,
  useGetDocumentsByProjectQuery,
  useGetProjectsQuery,
} from '../../redux/api-slice'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentProject,
  setCurrentProject,
  setModal,
} from '../../redux/application-slice'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import { ProjectDocument } from '../../shared/interfaces/project/document/document.interface'
import {
  setTemporaryHighlight,
  TemporaryHighlight,
} from '../../redux/secondary-viewer-slice'
import { useCommentViewerTabWidth } from '../../hooks/use-comment-viewer-tab-width'
import { useSecondaryCommentViewerTabWidth } from '../../hooks/use-secondary-comment-viewer-tab-width'
import useWindowDimensions from '../../hooks/use-window-dimensions'
import { usePostHog } from 'posthog-js/react'
import { POSTHOG } from '../../utils/posthog-constants'
import Diff from '../workflows/comment-table/diff'
import { isRatio } from '../../utils/convert-quads'
import SecondaryDocumentChangeViewerSimple from '../secondary-document-change-viewer-simple'
import DocumentChangeViewerSimple from '../document-change-viewer-simple'
import {
  DocumentBlackline,
  DocumentBlacklineSegment,
} from '../../shared/interfaces/project/document/blackline/document-blackline.interface'
import {
  ChatBubbleLeftIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { DocumentViewerContext } from '../../contexts/document-viewer-instance-context'
import { DocumentViewerCommentContext } from '../../contexts/document-viewer-comment-instance-context'
import { selectCurrentPage } from '../../redux/secondary-viewer-slice'
import DiffCreateComment from '../overlay/diff-create-comment'
import DiffCommentComponent from '../overlay/diff-comment-component'
import { setCurrentPage } from '../../redux/secondary-viewer-slice'
import { MODAL_TYPES } from '../modals/modal-controller'
import ExportPopover from '../workflows/workflow-components/export-popover'

interface DocumentChangePageNumberProps {
  document: ProjectDocument
  documentViewer
  secondaryDocumentViewer
  setPage: (page: number) => void
  page: number
}

const DocumentChangePageNumber: React.FC<DocumentChangePageNumberProps> = ({
  document,
  documentViewer,
  secondaryDocumentViewer,
  setPage,
  page,
}) => {
  const ref = createRef<HTMLInputElement>()
  const [localPage, setLocalPage] = useState(page)
  const onPressEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      documentViewer?.setCurrentPage(localPage, false)
      secondaryDocumentViewer?.setCurrentPage(localPage, false)
      setPage(parseInt(localPage.toString()))
      ref?.current?.blur()
    }
  }
  const onPageChange = (event) => {
    setLocalPage(event.target.value)
  }

  useEffect(() => {
    setLocalPage(page)
  }, [page])

  return (
    <div className="justify flex items-center">
      Page
      <div
        className={
          'relative flex h-8 items-center gap-2 rounded-lg border-2 border-[#ffffff00]  py-1 shadow-sm transition-colors hover:border-2 hover:border-blue-500 hover:!bg-[#fff]'
        }
      >
        <div>
          <input
            disabled={!document?.id}
            ref={ref}
            max={document?.total_pages}
            min={1}
            onChange={onPageChange}
            onKeyUp={onPressEnter}
            className={
              'cursor-select w-8 rounded-lg border-2 border-[#ffffff00] bg-[#ffffff00] p-0 text-center opacity-75 hover:border-2 hover:border-blue-700 focus:bg-none focus:outline-none active:!border-0 active:!border-[#ffffff00]'
            }
            type="text"
            value={localPage}
          />
        </div>
        <div className="whitespace-nowrap">/ {document?.total_pages}</div>
      </div>
    </div>
  )
}

interface DocumentChangeViewerDiffCardProps {
  page: number
  v2Document: ProjectDocument
  v2Segment: DocumentBlacklineSegment
  selectedBlacklineSegment: DocumentBlacklineSegment | null
  setSelectedBlacklineSegment: (
    selectedBlacklineSegment: DocumentBlacklineSegment | null
  ) => void
}

const DocumentChangeViewerDiffCard: React.FC<
  DocumentChangeViewerDiffCardProps
> = ({
  page,
  v2Document,
  v2Segment,
  setSelectedBlacklineSegment,
  selectedBlacklineSegment,
}) => {
  const { currentData: blacklineSegments } = useGetDocumentBlacklinesQuery(
    v2Document?.v1_document
      ? { page, documentID: v2Document?.v1_document ?? 0 }
      : skipToken
  )
  const v1Segment = useMemo(() => {
    if (!blacklineSegments) {
      return null
    }
    const match_id = v2Segment.match_id
    const v1blacklineSegment = blacklineSegments?.find(
      (s) => s.match_id === match_id && s.id !== v2Segment.id
    )

    if (!v1blacklineSegment) {
      return null
    }
    return v1blacklineSegment
  }, [blacklineSegments, v2Segment.id, v2Segment.match_id])

  const isExpanded = useMemo(() => {
    return selectedBlacklineSegment?.id === v2Segment.id
  }, [selectedBlacklineSegment, v2Segment.id])

  const onClick = () => {
    if (selectedBlacklineSegment?.id !== v2Segment.id) {
      setSelectedBlacklineSegment(v2Segment)
    } else {
      setSelectedBlacklineSegment(null)
    }
  }

  return v1Segment?.text?.trim() || v2Segment?.text?.trim() ? (
    <div className="w-full border">
      <button
        onClick={onClick}
        className={`my-1 w-full flex-col items-start justify-between rounded border-gray-200 px-3 text-left text-sm ${
          isExpanded ? '' : 'max-h-16 overflow-hidden overflow-ellipsis'
        }`}
      >
        <div className={`flex justify-between text-xs`}>
          <div
            className={`text-gray-500 ${selectedBlacklineSegment?.id === v2Segment.id ? 'font-bold' : ''}`}
          >
            Change:
          </div>
          <div className="flex items-center gap-1">
            <ChatBubbleLeftIcon className="h-4 w-4" />
            <div className="text-xs text-gray-500">
              {v2Segment?.comments?.length}
            </div>
          </div>
        </div>
        <div className="flex w-full items-start justify-between">
          <div>
            <Diff
              inputA={v1Segment?.text ?? ''}
              inputB={v2Segment?.text ?? ''}
            />
          </div>
          <div>
            <ChevronDownIcon
              className={`h-4 w-4 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>
      {isExpanded ? (
        <div className="flex flex-col p-3 text-xs">
          <div>
            Comments:
            {v2Segment?.comments?.map((comment) => {
              return (
                <DiffCommentComponent
                  key={comment.id}
                  documentBlacklineSegmentComment={comment}
                  segment={v2Segment}
                />
              )
            })}
            <DiffCreateComment segment={v2Segment} />
          </div>
        </div>
      ) : null}
    </div>
  ) : null
}

const DocumentChangeViewer: React.FC = () => {
  const currentPage = useSelector(selectCurrentPage)
  const { width } = useWindowDimensions()
  const { tabWidth, handler } = useCommentViewerTabWidth()
  const {
    tabWidth: secondaryTabWidth,
    handler: secondaryHandler,
    commentViewerCenterWidth,
  } = useSecondaryCommentViewerTabWidth()
  const { projectId, documentId } = useParams()
  const documentViewerContext = useContext(DocumentViewerContext)
  const documentViewerSecondaryContext = useContext(
    DocumentViewerCommentContext
  )
  const { documentViewer } = documentViewerContext
  const { documentViewer: documentViewerSecondary } =
    documentViewerSecondaryContext
  const { data: document } = useGetDocumentByUuidQuery(documentId ?? skipToken)
  const { data: documentBlacklines } = useGetDocumentBlacklineQuery(
    document?.id ?? skipToken
  )
  const { data: blacklineStats } = useGetDocumentBlacklineStatsQuery(
    document?.id ?? skipToken
  )
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (!blacklineStats) {
      return
    }
    if (searchParams.get('page')) {
      dispatch(setCurrentPage(parseInt(searchParams.get('page') ?? '1')))
      return
    }
    setSearchParams({
      page: Math.min(...(blacklineStats?.pages ?? [1])).toString(),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blacklineStats, dispatch])
  const page = useMemo(() => {
    return parseInt(searchParams.get('page') ?? '1')
  }, [searchParams])
  const {
    currentData: blacklineSegments,
    isFetching: isFetchingBlacklineSegments,
  } = useGetDocumentBlacklinesQuery(
    document?.v1_document
      ? { page, documentID: document?.v1_document ?? 0 }
      : skipToken
  )
  const { data: projects } = useGetProjectsQuery(undefined)
  const currentProject = useSelector(selectCurrentProject)
  const [selectedDocumentForSource, setSelectedDocumentForSource] =
    useState<ProjectDocument | null>(null)
  const [selectedSecondDocumentForSource, setSelectedSecondDocumentForSource] =
    useState<ProjectDocument | null>(document ?? null)
  const [selectedBlacklineSegment, setSelectedBlacklineSegment] =
    useState<DocumentBlacklineSegment | null>(null)

  const { data: documents } = useGetDocumentsByProjectQuery(
    currentProject ? { projectId: currentProject?.id } : skipToken
  )

  const posthog = usePostHog()

  const v2PageSegments = useMemo(() => {
    const segments = blacklineSegments
      ?.filter(
        (s) =>
          s.page === page &&
          s.match_id !== '-1' &&
          (s.document_blackline as DocumentBlackline).document?.id ===
            document?.id
      )
      .sort((a, b) => (a.quads?.[0]?.y1 ?? 0) - (b.quads?.[0]?.y1 ?? 0))
    return segments
  }, [blacklineSegments, document?.id, page])

  useEffect(() => {
    if (!document?.v1_document) {
      return
    }
    for (const d of documents?.documents ?? []) {
      if (document.v1_document?.toString() === d?.id?.toString()) {
        setSelectedDocumentForSource(d)
        break
      }
    }
  }, [document, documents])

  useEffect(() => {
    if (!document) {
      return
    }
    setSelectedSecondDocumentForSource(document)
  }, [document])

  useEffect(() => {
    posthog.capture(POSTHOG.duplicator_opened, {
      project_uuid: currentProject?.uuid,
    })
  }, [posthog, currentProject])

  useEffect(() => {
    if (projects && projectId) {
      const projectMatch = projects.find((p) => p.uuid === projectId)
      if (projectMatch) {
        dispatch(setCurrentProject(projectMatch))
      }
    }
  }, [projects, dispatch, projectId])

  const onSetPage = useCallback(
    (page: number) => {
      documentViewer?.setCurrentPage(page, false)
      documentViewerSecondary?.setCurrentPage(page, false)
      searchParams.set('page', page.toString())
      setSearchParams(searchParams)
    },
    [documentViewer, documentViewerSecondary, searchParams, setSearchParams]
  )

  const onClickPrevious = useCallback(() => {
    if (page === 1) {
      return
    }
    const previousPage = page - 1
    onSetPage(previousPage)
  }, [page, onSetPage])

  const onClickNext = useCallback(() => {
    if (page === document?.total_pages) {
      return
    }
    const nextPage = page + 1
    onSetPage(nextPage)
  }, [page, document?.total_pages, onSetPage])

  useEffect(() => {
    setTimeout(() => {
      if (!documentBlacklines) {
        return
      }
      if (currentPage && currentPage !== page) {
        if (currentPage > page) {
          onSetPage(currentPage)
        } else {
          onSetPage(currentPage)
        }
      }
    }, 100)
  }, [currentPage, page, onSetPage, documentBlacklines])

  useEffect(() => {
    const highlights: TemporaryHighlight[] = []

    v2PageSegments?.forEach((segment) => {
      const quadsWithPage = segment.quads?.map((quad) => {
        return {
          ...quad,
          page: segment.page,
        }
      })
      highlights.push({
        page: segment.page,
        quads: quadsWithPage ?? [],
        id: `highlight_${segment.id}`,
        pdfCoords: isRatio(quadsWithPage ?? []),
      })
    })
    dispatch(setTemporaryHighlight(highlights))
  }, [v2PageSegments, dispatch])

  const onSelectBlacklineSegment = useCallback(
    (segment: DocumentBlacklineSegment | null) => {
      if (!segment) {
        setSelectedBlacklineSegment(null)
        return
      }
      setSelectedBlacklineSegment(segment)
      const v1Segment = blacklineSegments?.find(
        (s) => s.match_id === segment.match_id && s.id !== segment.id
      )
      if (v1Segment) {
        documentViewer?.setCurrentPage(v1Segment.page, false)
      }
      documentViewerSecondary?.setCurrentPage(segment.page, false)
    },
    [blacklineSegments, documentViewerSecondary, documentViewer]
  )

  const v1DocumentFile = useMemo(() => {
    if (!documentBlacklines || !documentBlacklines?.length) {
      return
    }

    return (
      documentBlacklines?.find(
        (b) =>
          b.blackline_type === 0 &&
          (b.document?.id?.toString() === document?.v1_document?.toString() ||
            b.document?.id?.toString() === document?.id?.toString())
      )?.file ?? null
    )
  }, [documentBlacklines, document])

  const v2DocumentFile = useMemo(() => {
    if (!documentBlacklines || !documentBlacklines?.length) {
      return
    }
    return (
      documentBlacklines?.find(
        (b) =>
          b.blackline_type === 1 &&
          b.document?.id?.toString() === document?.id?.toString()
      )?.file ?? null
    )
  }, [documentBlacklines, document?.id])

  const onExportSegments = useCallback(async () => {
    dispatch(
      setModal({
        modal: MODAL_TYPES.EXPORT_BLACKLINE_OPTIONS,
      })
    )
  }, [dispatch])

  return (
    <div className="flex">
      <div
        style={{
          maxWidth: '100vw',
          height: 'calc(100vh - 50px)',
        }}
        className="flex w-full justify-between overflow-hidden"
      >
        <div className="flex flex-1 flex-grow">
          {documents && v1DocumentFile && (
            <DocumentChangeViewerSimple
              tabWidth={tabWidth ?? width / 3}
              overlay={true}
              selectedDocument={
                selectedDocumentForSource
                  ? {
                      ...selectedDocumentForSource,
                      file: v1DocumentFile ?? '',
                      optimized_file: v1DocumentFile ?? '',
                    }
                  : null
              }
            />
          )}
          <div className="relative flex">
            <button
              className={
                'text-md flex cursor-col-resize select-none items-center border border-t-[0px] border-[#dbdee3]  bg-[#f2f5fb]  px-0.5 text-xs transition-colors hover:border-blue-500 hover:bg-blue-300 hover:text-blue-500'
              }
              onMouseDown={handler}
            >
              ≡
            </button>
          </div>
        </div>
        <div
          style={{
            width: commentViewerCenterWidth ?? width / 3,
          }}
          className="flex h-full w-3/12 flex-col overflow-auto border-x bg-white"
        >
          <div>
            <div className="text-md flex w-full justify-center bg-gray-200 font-semibold">
              {document ? (
                <DocumentChangePageNumber
                  document={document}
                  documentViewer={documentViewer}
                  secondaryDocumentViewer={documentViewerSecondary}
                  setPage={onSetPage}
                  page={page}
                />
              ) : null}
            </div>

            <div className="flex w-full justify-end">
              <ExportPopover
                onClickExportToExcel={onExportSegments}
                exportTypes={['excel']}
                compact={true}
              />
            </div>
            <div className="p-3">
              <div className="flex w-full justify-between">
                <button
                  className="basis-1/6 rounded border py-2"
                  onClick={onClickPrevious}
                >
                  Previous
                </button>
                <button
                  className="basis-1/6 rounded border py-2"
                  onClick={onClickNext}
                >
                  Next
                </button>
              </div>
            </div>
            <div className="h-[calc(100vh-200px)] overflow-y-auto">
              {document && !isFetchingBlacklineSegments ? (
                v2PageSegments?.map((segment) => {
                  return (
                    <DocumentChangeViewerDiffCard
                      key={segment.id}
                      page={page}
                      v2Document={document}
                      v2Segment={segment}
                      setSelectedBlacklineSegment={onSelectBlacklineSegment}
                      selectedBlacklineSegment={selectedBlacklineSegment}
                    />
                  )
                })
              ) : (
                <div className="my-2 flex h-48 w-full items-center justify-center rounded bg-white text-center text-sm text-gray-700 shadow-sm">
                  <div className="mx-1 w-full">
                    <div className="my-3 h-3 w-full animate-pulse rounded border-l bg-gray-200" />
                    <div className="my-3 mt-10 h-3 w-1/3 animate-pulse rounded border-l bg-gray-200" />
                    <div className="my-3 h-3 w-full animate-pulse rounded border-l bg-gray-200" />
                    <div className="my-3 mt-10 h-3 w-1/3 animate-pulse rounded border-l bg-gray-200" />
                    <div className="my-3 h-3 w-full animate-pulse rounded border-l bg-gray-200" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-grow">
          <div className="relative flex">
            <button
              className={
                'text-md flex cursor-col-resize select-none items-center border border-t-[0px] border-[#dbdee3]  bg-[#f2f5fb]  px-0.5 text-xs transition-colors hover:border-blue-500 hover:bg-blue-300 hover:text-blue-500'
              }
              onMouseDown={secondaryHandler}
            >
              ≡
            </button>
          </div>
          {documents && v2DocumentFile && (
            <SecondaryDocumentChangeViewerSimple
              tabWidth={secondaryTabWidth ?? width / 3}
              overlay={true}
              selectedDocument={
                selectedSecondDocumentForSource
                  ? {
                      ...selectedSecondDocumentForSource,
                      file: v2DocumentFile ?? '',
                      optimized_file: v2DocumentFile ?? '',
                    }
                  : null
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default DocumentChangeViewer
